'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { useAuth, type SubscriptionPlan } from '@/contexts/AuthContext';

// ============================================================
// PLAN DEFINITIONS
// ============================================================

export const PLAN_LIMITS = {
  free: {
    maxAssets: 5,
    maxLiabilities: 5,
    maxGoals: 1,
    snapshots: false,
    charts: false,
    whatIfSimulator: false,
    pdfExport: false,
    excelExport: false,
    jsonBackup: false,
    multipleAccounts: false,
    healthScore: false,
    smartAlerts: false,
    monthlyReports: false,
    ads: true,
  },
  pro: {
    maxAssets: Infinity,
    maxLiabilities: Infinity,
    maxGoals: Infinity,
    snapshots: true,
    charts: true,
    whatIfSimulator: true,
    pdfExport: true,
    excelExport: false,
    jsonBackup: false,
    multipleAccounts: false,
    healthScore: false,
    smartAlerts: false,
    monthlyReports: true,
    ads: false,
  },
  pro_max: {
    maxAssets: Infinity,
    maxLiabilities: Infinity,
    maxGoals: Infinity,
    snapshots: true,
    charts: true,
    whatIfSimulator: true,
    pdfExport: true,
    excelExport: true,
    jsonBackup: true,
    multipleAccounts: true,
    healthScore: true,
    smartAlerts: true,
    monthlyReports: true,
    ads: false,
  },
} as const;

export type PlanFeature = keyof typeof PLAN_LIMITS.free;

export const PLAN_PRICES = {
  free: { monthly: 0, label: 'Free' },
  pro: { monthly: 4.99, label: 'Pro' },
  pro_max: { monthly: 12.99, label: 'Pro Max' },
};

export const STRIPE_PRICE_IDS = {
  pro: process.env.NEXT_PUBLIC_STRIPE_PRO_PRICE_ID || '',
  pro_max: process.env.NEXT_PUBLIC_STRIPE_PRO_MAX_PRICE_ID || '',
};

// ============================================================
// TRIAL HELPERS
// ============================================================

export function getTrialDaysRemaining(trialEndsAt: string | null): number {
  if (!trialEndsAt) return 0;
  const now = new Date();
  const end = new Date(trialEndsAt);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function isTrialActive(trialEndsAt: string | null, status: string): boolean {
  if (status !== 'trialing') return false;
  if (!trialEndsAt) return false;
  return new Date(trialEndsAt) > new Date();
}

// ============================================================
// PERMISSION CONTEXT
// ============================================================

interface PermissionContextType {
  plan: SubscriptionPlan;
  isActive: boolean;
  canAccess: (feature: PlanFeature) => boolean;
  getLimit: (feature: 'maxAssets' | 'maxLiabilities' | 'maxGoals') => number;
  requiresPlan: (feature: PlanFeature) => SubscriptionPlan | null;
  planLabel: string;
  isPro: boolean;
  isProMax: boolean;
  isFree: boolean;
  isTrial: boolean;
  trialDaysRemaining: number;
  trialEndsAt: string | null;
  hasUsedTrial: boolean;
  isAdmin: boolean;
}

const PermissionContext = createContext<PermissionContextType>({} as PermissionContextType);

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionProvider');
  return ctx;
};

export const PermissionProvider = ({ children }: { children: React.ReactNode }) => {
  const { profile } = useAuth();

  const isAdmin = useMemo(() => {
    return profile?.isAdmin === true;
  }, [profile]);

  const isTrial = useMemo(() => {
    if (!profile) return false;
    if (isAdmin) return false;
    return isTrialActive(profile.trialEndsAt, profile.subscriptionStatus);
  }, [profile, isAdmin]);

  const plan: SubscriptionPlan = useMemo(() => {
    if (!profile) return 'free';
    // Admin always gets Pro Max
    if (isAdmin) return 'pro_max';
    const status = profile.subscriptionStatus;
    if (status === 'trialing' && isTrial) {
      return profile.subscriptionPlan;
    }
    if (status === 'active') {
      return profile.subscriptionPlan;
    }
    return 'free';
  }, [profile, isTrial, isAdmin]);

  const isActive = useMemo(() => {
    if (!profile) return false;
    // Admin is always active
    if (isAdmin) return true;
    return profile.subscriptionStatus === 'active' || (profile.subscriptionStatus === 'trialing' && isTrial);
  }, [profile, isTrial, isAdmin]);

  const trialDaysRemaining = useMemo(() => {
    if (!profile) return 0;
    return getTrialDaysRemaining(profile.trialEndsAt);
  }, [profile]);

  const hasUsedTrial = useMemo(() => {
    if (!profile) return false;
    return profile.trialStartedAt !== null;
  }, [profile]);

  const canAccess = (feature: PlanFeature): boolean => {
    // Admin bypasses all feature checks
    if (isAdmin) return true;
    const limits = PLAN_LIMITS[plan];
    const val = limits[feature];
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return (val as number) > 0;
    return false;
  };

  const getLimit = (feature: 'maxAssets' | 'maxLiabilities' | 'maxGoals'): number => {
    // Admin has no limits
    if (isAdmin) return Infinity;
    return PLAN_LIMITS[plan][feature] as number;
  };

  const requiresPlan = (feature: PlanFeature): SubscriptionPlan | null => {
    // Admin never requires an upgrade
    if (isAdmin) return null;
    const freeVal = PLAN_LIMITS.free[feature];
    if (freeVal === true || (typeof freeVal === 'number' && (freeVal as number) > 0)) return null;
    const proVal = PLAN_LIMITS.pro[feature];
    if (proVal === true || (typeof proVal === 'number' && (proVal as number) > 0)) return 'pro';
    return 'pro_max';
  };

  const value: PermissionContextType = {
    plan,
    isActive,
    canAccess,
    getLimit,
    requiresPlan,
    planLabel: isAdmin ? 'Admin' : PLAN_PRICES[plan].label,
    isPro: plan === 'pro',
    isProMax: plan === 'pro_max' || isAdmin,
    isFree: !isAdmin && plan === 'free',
    isTrial,
    trialDaysRemaining,
    trialEndsAt: profile?.trialEndsAt ?? null,
    hasUsedTrial,
    isAdmin,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
